import { CheckCircleFilled } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { Visit } from "interface/visit"
import { useSendLoginLink } from "services/visits"
import { useStepCompletion } from "./useStepCompletion"

export function StepSendLoginLink({ visit }: { visit: Visit }) {
	const sms = useSendLoginLink(visit.id)
	const email = useSendLoginLink(visit.id)
	const { sendLink, markStepAs } = useStepCompletion(visit)
	const supportsTextMessages =
		!!visit.patient?.phoneNumber?.supportsTextMessages
	const hasEmail = !!visit.patient?.email
	const fullName = visit.patient?.fullName
	const isComplete = sendLink || visit.status === "COMPLETED"

	return (
		<div className="flex size-full grow flex-col items-center justify-center gap-3 text-center">
			{isComplete && (
				<>
					<CheckCircleFilled
						aria-hidden
						className="text-[48px] text-green-60"
					/>
					<span>The patient logged in</span>
				</>
			)}

			{!isComplete && (
				<>
					Confirm the patient is able to log in
					<div className="mt-auto flex size-full flex-col items-center justify-end gap-2">
						<ErrorStatus
							show={
								(sms.isError || email.isError) &&
								!sms.isPending &&
								!email.isPending
							}
						>
							Failed to send the link
						</ErrorStatus>
						<Wrap
							condition={!supportsTextMessages}
							with={(children) => (
								<Tooltip
									title={`${fullName} does not have a textable phone number linked to their account`}
								>
									{children}
								</Tooltip>
							)}
						>
							<Button
								className="w-full"
								loading={sms.isPending && supportsTextMessages}
								disabled={!supportsTextMessages || sms.isPending}
								onClick={() => sms.mutate("sms")}
							>
								Send link via text
							</Button>
						</Wrap>
						<Wrap
							condition={!hasEmail}
							with={(children) => (
								<Tooltip
									title={`${fullName} does not have an email address linked to their account`}
								>
									{children}
								</Tooltip>
							)}
						>
							<Button
								className="w-full"
								loading={email.isPending && hasEmail}
								disabled={!hasEmail || email.isPending}
								onClick={() => email.mutate("email")}
							>
								Send link via email
							</Button>
						</Wrap>
						<Button
							type="primary"
							className="w-full"
							onClick={() => markStepAs("sendLink")}
						>
							Mark as complete
						</Button>
					</div>
				</>
			)}
		</div>
	)
}
